/**
 * Canonical money formatter for hand-written theme scripts.
 *
 * theme.js has the same helper but is an ES module and does not export it on
 * window, hence this standalone version for the defer-loaded scripts
 * (upsell-manager.js, discount-preview-renderer.js).
 *
 * Loaded in theme.liquid with defer BEFORE its consumers and depends on
 * window.themeVariables (set inline by js-variables.liquid).
 *
 * Exceptions that CANNOT use this helper because they run during HTML parsing
 * (before defer scripts execute):
 *   - snippets/discount-preview-inline-overlay.liquid (Intl-based, in <head>)
 *   - snippets/product-info.liquid inline block (sync during body parse)
 */
(function () {
  'use strict';

  window.Zahnheld = window.Zahnheld || {};
  if (window.Zahnheld.formatMoney) return;

  function defaultTo(value, fallback) {
    return value == null || value !== value ? fallback : value;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');

    if (isNaN(cents) || cents == null) {
      return '0';
    }

    const amount = (cents / 100).toFixed(precision);
    const parts = amount.split('.');
    const major = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const minor = parts[1] ? decimal + parts[1] : '';
    return major + minor;
  }

  /**
   * Format a price in cents according to a Shopify money format string.
   *
   * @param {number|string} cents - Integer cents (e.g. 1234 for 12.34) or an
   *   integer-as-string. Strings have a single dot stripped before parsing so
   *   Liquid-rendered prices like "12.34" (already in cents in some contexts)
   *   are handled defensively.
   * @param {string} [format] - Shopify money format string with a
   *   `{{ amount }}` placeholder (e.g. `'{{amount_with_comma_separator}} €'`).
   *   Defaults to `window.themeVariables.settings.moneyFormat`, then to
   *   `'{{amount}} €'`.
   * @returns {string} Formatted price string. Returns `'0'` for NaN/null input.
   */
  window.Zahnheld.formatMoney = function (cents, format) {
    let amountCents = cents;
    if (typeof amountCents === 'string') {
      amountCents = Number(amountCents.replace('.', ''));
    }

    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    const formatString = format || settings.moneyFormat || '{{amount}} €';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    let value;

    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = formatWithDelimiters(amountCents, 2); break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amountCents, 0); break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(amountCents, 2, ' ', '.'); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amountCents, 2, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amountCents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amountCents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(amountCents, 0, ' '); break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(amountCents, 0, "'"); break;
      default:
        value = formatWithDelimiters(amountCents, 2); break;
    }

    return formatString.replace(placeholderRegex, value);
  };
})();
